import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ChevronLeft, MapPin, Clock, DollarSign } from 'lucide-react';
import { motion } from 'framer-motion';
import AddressAutocompleteInput from '@/components/AddressAutocompleteInput';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CreateJobPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [addressData, setAddressData] = useState(null);  // Full address object with lat/lng

  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    urgency: 'today',
    description: '',
    address_id: '',
    photos: []
  });

  const categories = [
    { name: 'Plumbing', subcategories: ['Toilet leaking', 'Sink blocked', 'Pipe burst', 'Boiler issue'] },
    { name: 'Electrical', subcategories: ['Light not working', 'Socket issue', 'Circuit breaker', 'Wiring problem'] },
    { name: 'Heating', subcategories: ['Boiler repair', 'Radiator issue', 'Thermostat problem', 'No heating'] },
    { name: 'Handyman', subcategories: ['Door repair', 'Furniture assembly', 'Painting', 'General maintenance'] },
    { name: 'Carpentry', subcategories: ['Cabinet repair', 'Shelf installation', 'Door fitting', 'Window repair'] },
  ];

  const urgencyOptions = [
    { value: 'now', label: 'Now (30-60 mins)', multiplier: 1.5 },
    { value: 'today', label: 'Today (2-4 hours)', multiplier: 1.2 },
    { value: 'scheduled', label: 'Schedule later', multiplier: 1.0 },
  ];

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API_URL}/addresses`);
      setAddresses(response.data);
    } catch (error) {
      console.error('Failed to load addresses');
    }
  };

  // Handle address selection from autocomplete
  const handleAddressSelected = (address) => {
    setAddressData(address);
  };

  const calculatePricing = async () => {
    // Validate address data (lat/lng required)
    if (!formData.category || !formData.subcategory || !formData.description) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (!addressData || !addressData.lat || !addressData.lng) {
      toast.error('Please select a valid address from the suggestions');
      return;
    }

    setLoading(true);
    try {
      // Map country to currency
      const currencyMap = {
        'UK': 'GBP',
        'United Kingdom': 'GBP',
        'US': 'USD',
        'USA': 'USD',
        'United States': 'USD',
        'France': 'EUR',
        'Germany': 'EUR',
        'Spain': 'EUR',
        'Italy': 'EUR',
        'Netherlands': 'EUR',
        'Belgium': 'EUR'
      };
      
      const currency = currencyMap[addressData.country] || 'GBP';
      
      const pricingRequest = {
        category: formData.category,
        subcategory: formData.subcategory,
        description: formData.description,
        urgency: formData.urgency,
        country: addressData.country || 'United Kingdom',
        city: addressData.city,
        latitude: addressData.lat,
        longitude: addressData.lng,
        distance_km: 5.0,
        photos: formData.photos,
        currency: currency
      };

      const response = await axios.post(`${API_URL}/pricing/calculate`, pricingRequest);
      setPricing(response.data);
      
      // Update formData with currency for job creation
      setFormData({ ...formData, currency: currency });
      
      setStep(3);
    } catch (error) {
      toast.error('Failed to calculate pricing');
    } finally {
      setLoading(false);
    }
  };

  const createJob = async () => {
    setLoading(true);
    try {
      // Create job with estimated price and address data
      const jobData = {
        category: formData.category,
        subcategory: formData.subcategory,
        description: formData.description,
        urgency: formData.urgency,
        photos: formData.photos,
        // Address data
        address_text: addressData.formattedAddress,
        latitude: addressData.lat,
        longitude: addressData.lng,
        place_id: addressData.placeId,
        postcode: addressData.postcode,
        city: addressData.city,
        country: addressData.country || 'United Kingdom',
        // Pricing
        estimated_price: pricing?.breakdown.total || 0,
        currency: pricing?.breakdown.currency || 'GBP'
      };
      
      const response = await axios.post(`${API_URL}/jobs`, jobData);
      const job = response.data;

      toast.success('Job created!');
      navigate(`/jobs/${job.id}`);
    } catch (error) {
      toast.error('Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <Label>What type of job?</Label>
        <Select value={formData.category} onValueChange={(value) => {
          setFormData({ ...formData, category: value, subcategory: '' });
        }}>
          <SelectTrigger data-testid="category-select" className="h-12">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map(cat => (
              <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {formData.category && (
        <div>
          <Label>What exactly is the issue?</Label>
          <Select value={formData.subcategory} onValueChange={(value) => setFormData({ ...formData, subcategory: value })}>
            <SelectTrigger data-testid="subcategory-select" className="h-12">
              <SelectValue placeholder="Select issue" />
            </SelectTrigger>
            <SelectContent>
              {categories.find(c => c.name === formData.category)?.subcategories.map(sub => (
                <SelectItem key={sub} value={sub}>{sub}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label>How urgent?</Label>
        <Select value={formData.urgency} onValueChange={(value) => setFormData({ ...formData, urgency: value })}>
          <SelectTrigger data-testid="urgency-select" className="h-12">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {urgencyOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button
        data-testid="next-step-btn"
        onClick={() => setStep(2)}
        disabled={!formData.category || !formData.subcategory}
        className="w-full bg-[#007AFF] hover:bg-[#0066CC] text-white rounded-full h-12"
      >
        Next
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <Label>Describe the problem</Label>
        <Textarea
          data-testid="description-textarea"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Tell us more about what needs fixing..."
          className="h-32"
        />
      </div>

      <div>
        <Label>Where is this?</Label>
        <AddressAutocompleteInput
          value={addressData?.formattedAddress || ''}
          onAddressSelected={handleAddressSelected}
          placeholder="Enter your address (worldwide)"
          countryRestriction={null}
          showRecentAddresses={true}
          showSavedAddresses={true}
          token={token}
          testId="create-job-address"
        />
      </div>

      <div className="flex gap-4">
        <Button
          variant="outline"
          onClick={() => setStep(1)}
          className="flex-1"
        >
          Back
        </Button>
        <Button
          data-testid="calculate-price-btn"
          onClick={calculatePricing}
          disabled={loading || !formData.description || !addressData?.lat}
          className="flex-1 bg-[#007AFF] hover:bg-[#0066CC] text-white rounded-full"
        >
          {loading ? 'Calculating...' : 'Get Price'}
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="bg-neutral-50 rounded-xl p-6">
        <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Price Breakdown</h3>
        
        {pricing && (
          <>
            <div className="space-y-3">
              {/* Diagnostic Fee - always shown, always charged */}
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-neutral-600">On-Site Diagnostic Fee</span>
                  <span className="text-xs text-neutral-400 block">Charged at booking to dispatch contractor</span>
                </div>
                <span className="font-semibold">{getCurrencySymbol(pricing.breakdown.currency)}{(pricing.breakdown.diagnostic_fee_amount || pricing.breakdown.callout_fee || 0).toFixed(2)}</span>
              </div>
              
              {/* Labor estimate */}
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-neutral-600">Labour estimate range</span>
                  <span className="text-xs text-neutral-400 block">Confirmed after on-site assessment</span>
                </div>
                <span className="font-semibold">
                  {getCurrencySymbol(pricing.breakdown.currency)}{(pricing.breakdown.estimate_labour_low || pricing.breakdown.labor_estimate || 0).toFixed(0)}–{(pricing.breakdown.estimate_labour_high || pricing.breakdown.labor_estimate || 0).toFixed(0)}
                </span>
              </div>
              
              {/* Materials estimate */}
              {(pricing.breakdown.materials_estimate > 0 || pricing.breakdown.estimate_materials_high > 0) && (
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-neutral-600">Materials estimate range</span>
                    <span className="text-xs text-neutral-400 block">If parts or supplies are needed</span>
                  </div>
                  <span className="font-semibold">
                    {getCurrencySymbol(pricing.breakdown.currency)}{(pricing.breakdown.estimate_materials_low || 0).toFixed(0)}–{(pricing.breakdown.estimate_materials_high || pricing.breakdown.materials_estimate || 0).toFixed(0)}
                  </span>
                </div>
              )}
              
              {/* NO location_adjustment, urgency_adjustment, or travel_fee - removed from customer pricing */}
              
              {/* Total - Estimated range */}
              <div className="border-t border-neutral-200 pt-4 mt-4 flex justify-between items-center">
                <div>
                  <span className="text-lg font-bold">Estimated total range</span>
                  <span className="text-xs text-neutral-500 block">Final quote confirmed after on-site assessment</span>
                </div>
                <div className="text-right">
                  <span className="text-3xl font-bold text-[#007AFF]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {getCurrencySymbol(pricing.breakdown.currency)}{(pricing.breakdown.estimate_total_low || pricing.breakdown.total || 0).toFixed(0)}–{(pricing.breakdown.estimate_total_high || pricing.breakdown.total || 0).toFixed(0)}
                  </span>
                  <p className="text-xs text-neutral-500 mt-1">{pricing.breakdown.currency}</p>
                </div>
              </div>
              
              {/* No extra platform fees badge */}
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                <span className="text-sm text-green-700 font-medium">✓ No extra platform fees — ZOLT is funded from the Diagnostic Fee</span>
              </div>
            </div>

            {/* Estimate Confidence Indicator */}
            {pricing.ai_analysis && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${
                    pricing.ai_confidence >= 0.8 ? 'bg-green-500' : 
                    pricing.ai_confidence >= 0.5 ? 'bg-yellow-500' : 'bg-red-500'
                  }`} />
                  <span className="text-sm font-medium text-neutral-700">
                    Estimate confidence: {Math.round(pricing.ai_confidence * 100)}%
                  </span>
                </div>
                <p className="text-xs text-neutral-500">
                  {pricing.ai_analysis.complexity_tier === 'low' && 'This is a straightforward job with predictable costs.'}
                  {pricing.ai_analysis.complexity_tier === 'medium' && 'Standard complexity - final quote may vary based on assessment.'}
                  {pricing.ai_analysis.complexity_tier === 'high' && 'Complex job - final quote confirmed after on-site assessment.'}
                </p>
                {pricing.ai_analysis.reasoning && (
                  <p className="text-xs text-neutral-600 mt-2 italic">&ldquo;{pricing.ai_analysis.reasoning}&rdquo;</p>
                )}
              </div>
            )}
            
            {/* Important notes */}
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                <strong>Note:</strong> The Diagnostic Fee is charged at booking to dispatch a contractor. The final quote is confirmed after on-site assessment and requires your approval before work begins.
              </p>
            </div>
          </>
        )}
      </div>

      <div className="flex gap-4">
        <Button
          variant="outline"
          onClick={() => setStep(2)}
          className="flex-1"
        >
          Back
        </Button>
        <Button
          data-testid="confirm-job-btn"
          onClick={createJob}
          disabled={loading}
          className="flex-1 bg-[#007AFF] hover:bg-[#0066CC] text-white rounded-full"
        >
          {loading ? 'Creating...' : 'Confirm & Book'}
        </Button>
      </div>
    </div>
  );

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'GBP': '£',
      'USD': '$',
      'EUR': '€',
      'AED': 'د.إ',
      'INR': '₹'
    };
    return symbols[currency] || currency;
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="text-neutral-600 hover:text-neutral-900">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Request a Zolt</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-12">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                s === step ? 'bg-[#007AFF] text-white' : s < step ? 'bg-green-500 text-white' : 'bg-neutral-200 text-neutral-600'
              }`}>
                {s}
              </div>
              {s < 3 && <div className="w-12 h-1 bg-neutral-200" />}
            </div>
          ))}
        </div>

        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-xl border border-neutral-200 p-8"
        >
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </motion.div>
      </div>
    </div>
  );
};

export default CreateJobPage;
